/**
 * B2C Reservation API Client
 * Only reads reservation data - does NOT create reservations
 * Updated to use variantId-based endpoint per B2C API Integration Guide
 */

(function() {
  'use strict';

  // Initialize or extend existing reservationAPI object
  if (!window.reservationAPI) {
    window.reservationAPI = {};
  }

  // Set baseUrl if not already set
  // test: https://1c.multi-form.com.ua/SinklitAPI/hs/shopify
  if (!window.reservationAPI.baseUrl) {
    window.reservationAPI.baseUrl = 'https://ut.sinklitbeauty-partners.com.ua/UT_Work_API/hs/shopify';
  }

  // Set debug if not already set
  if (typeof window.reservationAPI.debug === 'undefined') {
    window.reservationAPI.debug = false;
  }

  // Reservation cache for reducing API calls
  const reservationCache = new Map();

  /**
   * Get reserved quantity for a variant
   * GET /api/reservations/:variantId?excludeEmail={email}
   * 
   * @param {string} variantId - Shopify variant ID
   * @param {string} [excludeEmail] - Optional customer email to exclude
   * @param {number} [cacheTTL] - Cache TTL in milliseconds (default: 5000)
   * @returns {Promise<{variantId: string, totalReserved: number, excludedEmail?: string}>}
   */
  if (!window.reservationAPI.getReservedQuantity) {
    window.reservationAPI.getReservedQuantity = async function(variantId, excludeEmail, cacheTTL = 5000) {
      try {
        // Check cache first
        const cacheKey = `${variantId}:${excludeEmail || 'none'}`;
        const cached = reservationCache.get(cacheKey);
        
        if (cached && Date.now() - cached.timestamp < cacheTTL) {
          if (window.reservationAPI.debug) {
            console.log('💾 [Reservation API] Using cached data:', cached.data);
          }
          return cached.data;
        }

        // Build query parameters
        const queryParams = [];
        if (excludeEmail) {
          queryParams.push(`excludeEmail=${encodeURIComponent(excludeEmail)}`);
        }
        
        const url = `${window.reservationAPI.baseUrl}/api/reservations/${variantId}${queryParams.length > 0 ? '?' + queryParams.join('&') : ''}`;
        
        if (window.reservationAPI.debug) {
          console.log('🔍 [Reservation API] Fetching reserved quantity:', { variantId, excludeEmail });
          console.log('🌐 [Reservation API] URL:', url);
        }
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Normalize response format
        const result = {
          variantId: data.variantId || variantId,
          totalReserved: data.totalReserved || 0,
          excludedEmail: data.excludedEmail || (excludeEmail ? excludeEmail : null)
        };
        
        // Cache the result
        reservationCache.set(cacheKey, {
          data: result,
          timestamp: Date.now()
        });
        
        if (window.reservationAPI.debug) {
          console.log('✅ [Reservation API] Response:', result);
        }
        
        return result;
      } catch (error) {
        console.error('❌ [Reservation API] Error fetching reserved quantity:', error);
        // Fail-closed: throw error so caller can handle
        throw error;
      }
    };
  }

  /**
   * Calculate available quantity for a variant
   * 
   * @param {string} variantId - Shopify variant ID
   * @param {number} inventoryLimit - Total inventory available
   * @param {string} [excludeEmail] - Optional customer email to exclude
   * @param {number} [cacheTTL] - Cache TTL in milliseconds (default: 5000)
   * @returns {Promise<number>} Available quantity
   */
  if (!window.reservationAPI.getAvailableQuantity) {
    window.reservationAPI.getAvailableQuantity = async function(variantId, inventoryLimit, excludeEmail, cacheTTL = 5000) {
      try {
        const reserved = await window.reservationAPI.getReservedQuantity(variantId, excludeEmail, cacheTTL);
        const available = Math.max(0, inventoryLimit - reserved.totalReserved);
        
        if (window.reservationAPI.debug) {
          console.log('✅ [Reservation API] Available quantity:', {
            variantId,
            inventoryLimit,
            totalReserved: reserved.totalReserved,
            available
          });
        }
        
        return available;
      } catch (error) {
        console.error('❌ [Reservation API] Error calculating available quantity:', error);
        throw error;
      }
    };
  }

  /**
   * Test backend connection
   * GET /health
   * 
   * @returns {Promise<boolean>} True if backend is reachable
   */
  if (!window.reservationAPI.testConnection) {
    window.reservationAPI.testConnection = async function() {
      try {
        const response = await fetch(`${window.reservationAPI.baseUrl}/health`);
        if (response.ok) {
          const data = await response.json();
          if (window.reservationAPI.debug) {
            console.log('✅ [Reservation API] Backend connected:', data);
          }
          return true;
        }
        return false;
      } catch (error) {
        console.error('❌ [Reservation API] Backend connection failed:', error);
        return false;
      }
    };
  }

  /**
   * Show notification when cart items were removed (reserved in B2B / other cart)
   * Message in Ukrainian, includes product title(s)
   * @param {number} removedCount - Number of items removed
   * @param {Array<{title: string}>} [removedItems] - Optional list of removed items (each with title)
   */
  if (!window.reservationAPI.showCartAdjustedNotification) {
    window.reservationAPI.showCartAdjustedNotification = function(removedCount, removedItems) {
      if (typeof document === 'undefined' || removedCount <= 0) return;

      var titles = (removedItems && removedItems.length) ? removedItems.map(function(i) { return i.title || 'Товар'; }) : [];
      var titleText = titles.length === 1 ? ('«' + titles[0] + '»') : (titles.length > 1 ? titles.map(function(t) { return '«' + t + '»'; }).join(', ') : '');

      var message;
      if (removedCount === 1) {
        message = titleText
          ? 'Товар ' + titleText + ' видалено з кошика, оскільки він уже зарезервований в іншому кошику.'
          : 'Товар видалено з кошика, оскільки він уже зарезервований в іншому кошику.';
      } else {
        message = titleText
          ? 'Товари ' + titleText + ' видалено з кошика, оскільки вони вже зарезервовані в іншому кошику.'
          : 'Частину товарів видалено з кошика, оскільки вони вже зарезервовані в іншому кошику.';
      }

      alert(message);
    };
  }

  /**
   * Clear reservation cache
   * Useful for forcing fresh data after cart updates
   */
  if (!window.reservationAPI.clearCache) {
    window.reservationAPI.clearCache = function() {
      reservationCache.clear();
      if (window.reservationAPI.debug) {
        console.log('🗑️ [Reservation API] Cache cleared');
      }
    };
  }

  /**
   * Check cart items and remove reserved items
   * On page load, checks all cart items and removes any that are fully reserved
   * 
   * @returns {Promise<{removed: number, checked: number}>} Number of items removed and checked
   */
  if (!window.reservationAPI.checkAndRemoveReservedCartItems) {
    window.reservationAPI.checkAndRemoveReservedCartItems = async function() {
      console.log('🚀 [Cart Check] Starting cart reservation check...');
      try {
        // Skip if reservation API is not available
        if (!window.reservationAPI.baseUrl) {
          console.warn('⚠️ [Cart Check] Reservation API not available, skipping cart check');
          return { removed: 0, checked: 0 };
        }

        // Get cart route - fallback to /cart if theme.routes is not available
        const cartRoute = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
        const cartUrl = `${cartRoute}.js`;

        if (window.reservationAPI.debug) {
          console.log('🛒 [Cart Check] Fetching cart:', cartUrl);
        }

        // Fetch current cart
        const cartResponse = await fetch(cartUrl);
        if (!cartResponse.ok) {
          throw new Error(`Failed to fetch cart: ${cartResponse.status} ${cartResponse.statusText}`);
        }

        const cart = await cartResponse.json();
        
        if (!cart.items || cart.items.length === 0) {
          if (window.reservationAPI.debug) {
            console.log('🛒 [Cart Check] Cart is empty, nothing to check');
          }
          return { removed: 0, checked: 0 };
        }

        if (window.reservationAPI.debug) {
          console.log('🛒 [Cart Check] Checking', cart.items.length, 'cart items');
        }

        const itemsToRemove = [];
        const checkPromises = [];

        // Check each cart line item by making a request with its variant ID to the backend
        // For each item: Get variant ID → Check reservation status via backend API → Remove if reserved
        for (const item of cart.items) {
          const variantId = item.variant_id ? item.variant_id.toString() : null;
          const productHandle = item.handle || null;

          if (!variantId) {
            console.warn('⚠️ [Cart Check] Item missing variant_id:', item);
            continue;
          }

          if (!productHandle) {
            console.warn('⚠️ [Cart Check] Item missing product handle:', item);
            continue;
          }

          // Process each line item: fetch inventory, then check reservations via backend
          checkPromises.push(
            (async () => {
              try {
                // Step 1: Fetch inventory data for this product variant
                console.log(`📦 [Cart Check] Checking variant ${variantId} from product ${productHandle}`);
                
                const inventoryResponse = await fetch(`/products/${productHandle}?view=inventory`);
                if (!inventoryResponse.ok) {
                  throw new Error(`Failed to fetch inventory: ${inventoryResponse.status}`);
                }
                
                const inventoryData = await inventoryResponse.json();
                const variantData = inventoryData.variants?.find(v => v.id.toString() === variantId);
                
                if (!variantData) {
                  console.warn(`⚠️ [Cart Check] Variant ${variantId} not found in inventory data`);
                  return;
                }

                const inventoryQuantity = variantData.inventory_quantity || 0;
                const inventoryManagement = variantData.inventory_management;

                // Skip items without Shopify inventory tracking
                if (inventoryManagement !== 'shopify') {
                  console.log(`⏭️ [Cart Check] Skipping variant ${variantId} - inventory not managed by Shopify`);
                  return;
                }

                if (inventoryQuantity <= 0) {
                  console.log(`⏭️ [Cart Check] Skipping variant ${variantId} - no inventory quantity`);
                  return;
                }

                // Step 2: Make request to backend reservation API with variant ID
                // This is the key step: check each line item's variant ID against the backend
                console.log(`🔍 [Cart Check] Making backend request for variant ${variantId} (inventory: ${inventoryQuantity})`);
                
                const reservedData = await window.reservationAPI.getReservedQuantity(
                  variantId, 
                  window.customerEmail || null
                );

                // Step 3: Calculate available quantity based on backend response
                const available = Math.max(0, inventoryQuantity - reservedData.totalReserved);
                
                console.log(`📊 [Cart Check] Variant ${variantId} - Inventory: ${inventoryQuantity}, Reserved: ${reservedData.totalReserved}, Available: ${available}`);

                // Step 4: If available quantity is 0 or less, mark for removal
                if (available <= 0) {
                  console.log(`🗑️ [Cart Check] Marking variant ${variantId} for removal - no available quantity`);
                  itemsToRemove.push({
                    key: item.key,
                    variantId: variantId,
                    title: item.product_title || item.title || 'Item'
                  });
                } else {
                  console.log(`✅ [Cart Check] Variant ${variantId} is available (${available} remaining)`);
                }
              } catch (error) {
                console.error(`❌ [Cart Check] Error checking variant ${variantId}:`, error);
                // On error, don't remove the item (fail-safe)
              }
            })()
          );
        }

        // Wait for all checks to complete
        await Promise.all(checkPromises);

        // Remove reserved items
        let removedCount = 0;
        if (itemsToRemove.length > 0) {
          console.log(`🗑️ [Cart Check] Removing ${itemsToRemove.length} reserved items:`, itemsToRemove);

          const removePromises = itemsToRemove.map((item) => {
            console.log(`🔄 [Cart Check] Removing item: ${item.title} (key: ${item.key}, variant: ${item.variantId})`);
            return fetch(`${cartRoute}/change.js`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                id: item.key,
                quantity: 0
              })
            })
              .then((response) => {
                if (!response.ok) {
                  throw new Error(`Failed to remove item ${item.key}: ${response.status}`);
                }
                removedCount++;
                console.log(`✅ [Cart Check] Successfully removed reserved item: ${item.title} (${item.variantId})`);
                return response.json();
              })
              .catch((error) => {
                console.error(`❌ [Cart Check] Error removing item ${item.key}:`, error);
                throw error;
              });
          });

          await Promise.all(removePromises);

          console.log(`✅ [Cart Check] Removed ${removedCount} out of ${itemsToRemove.length} reserved items`);

          // Clear cache after removing items
          window.reservationAPI.clearCache();

          // Dispatch cart change event and reload cart markup so data-line-items updates
          if (removedCount > 0 && typeof document !== 'undefined') {
            console.log('🔄 [Cart Check] Fetching updated cart and dispatching change event...');
            // Fetch updated cart
            const updatedCartResponse = await fetch(cartUrl);
            if (updatedCartResponse.ok) {
              const updatedCart = await updatedCartResponse.json();
              document.dispatchEvent(new CustomEvent('theme:cart:change', {
                detail: { cart: updatedCart },
                bubbles: true
              }));
              // Trigger cart form HTML refresh (loadForm via theme:cart:reload)
              document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
              console.log('✅ [Cart Check] Cart change and reload dispatched');
            }
            // Show Ukrainian notification that products were removed (reserved in other cart)
            window.reservationAPI.showCartAdjustedNotification(removedCount, itemsToRemove);
          }
        } else {
          console.log('✅ [Cart Check] All cart items are available - nothing to remove');
        }

        return {
          removed: removedCount,
          checked: cart.items.length
        };
      } catch (error) {
        console.error('❌ [Cart Check] Error checking cart items:', error);
        return { removed: 0, checked: 0 };
      }
    };
  }

  // Log initialization
  if (window.reservationAPI.debug) {
    console.log('✅ [Reservation API] B2C Client Initialized:', {
      baseUrl: window.reservationAPI.baseUrl,
      methods: {
        getReservedQuantity: !!window.reservationAPI.getReservedQuantity,
        getAvailableQuantity: !!window.reservationAPI.getAvailableQuantity,
        testConnection: !!window.reservationAPI.testConnection,
        clearCache: !!window.reservationAPI.clearCache,
        checkAndRemoveReservedCartItems: !!window.reservationAPI.checkAndRemoveReservedCartItems
      }
    });
  }
})();

